import { BrowseCardProps } from "@components/molecules/BrowseCard/BrowseCard.types";
import { ApiFilterResponse } from "@interfaces/filter.types";
import assetLoader from "@utils/internalImageLoder";

export const transformApiBrowseCardData = (
  filterData: ApiFilterResponse | null
): BrowseCardProps[] => {
  if (!filterData) {
    return [];
  }

  const browseCardConfigs: BrowseCardProps[] = [];

  if (filterData.brand_model && filterData.brand_model.length > 0) {
    const uniqueBrands = filterData.brand_model.reduce(
      (acc: any[], brandModel) => {
        const existingBrand = acc.find(
          (brand) => brand.brand_id === brandModel.brand_id
        );
        if (!existingBrand) {
          acc.push({
            brand_id: brandModel.brand_id,
            brand_name: brandModel.brand_name,
            brand_value: brandModel.brand_value,
          });
        }
        return acc;
      },
      []
    );

    const brandOptions = uniqueBrands.map((brand) => ({
      label: brand.brand_name,
      value: brand.brand_value,
      icon: `${assetLoader()}/icons/car-brands-dark/${brand.brand_name}.svg`,
    }));

    browseCardConfigs.push({
      type: "brand",
      title: "Browse cars by Brands",
      options: brandOptions,
      showLimited: true,
      limitCount: 10,
    });
  }

  if (filterData.body_type && filterData.body_type.length > 0) {
    const bodyTypeOptions = filterData.body_type.map((bodyType) => ({
      label: bodyType.key || bodyType.value,
      value: bodyType.value.toLowerCase(),
      icon: `${assetLoader()}/icons/BrowseCard/${getBodyTypeIcon(
        bodyType.key || bodyType.value
      )}`,
    }));

    browseCardConfigs.push({
      type: "bodyType",
      title: "Browse cars by Body Type",
      options: bodyTypeOptions,
    });
  }

  if (filterData.fuel_type && filterData.fuel_type.length > 0) {
    const fuelTypeOptions = filterData.fuel_type.map((fuelType) => ({
      label: fuelType.key || fuelType.value,
      value: fuelType.value.toLowerCase(),
      icon: `${assetLoader()}/icons/BrowseCard/${getFuelTypeIcon(
        fuelType.key || fuelType.value
      )}`,
    }));

    browseCardConfigs.push({
      type: "fuelType",
      title: "Browse cars by Fuel Type",
      options: fuelTypeOptions,
    });
  }

  if (filterData.transmission && filterData.transmission.length > 0) {
    const transmissionOptions = filterData.transmission.map((transmission) => ({
      label: transmission.value,
      value: transmission.value.toLowerCase(),
      icon: `${assetLoader()}/icons/BrowseCard/${getTransmissionIcon(
        transmission.key || transmission.value
      )}`,
    }));

    browseCardConfigs.push({
      type: "transmission",
      title: "Browse cars by Transmission",
      options: transmissionOptions,
    });
  }

  if (filterData.budget_range && filterData.budget_range.length > 0) {
    const budgetRangeOptions = filterData.budget_range.map((budget) => ({
      label: budget.key || budget.value,
      value: budget.value,
    }));

    browseCardConfigs.push({
      type: "budget",
      title: "Browse cars by Price Range",
      options: budgetRangeOptions,
    });
  }

  if (filterData.seat_capacity && filterData.seat_capacity.length > 0) {
    const seatCapacityOptions = filterData.seat_capacity.map((seat) => ({
      label: seat.key || seat.value,
      value: seat.value,
    }));

    browseCardConfigs.push({
      type: "seatingCapacity",
      title: "Browse cars by Seating Capacity",
      options: seatCapacityOptions,
    });
  }

  return browseCardConfigs;
};

const getBodyTypeIcon = (bodyType: string): string => {
  const iconMap: Record<string, string> = {
    Hatchback: "hatchback.svg",
    SUV: "SUV.svg",
    Sedan: "sedan.svg",
    "COMPACT SEDAN": "sedan.svg",
    "MPV": "MPV.svg",
    MINIVAN_VAN: "MPV.svg",
    "PICK UP": "SUV.svg",
    CONVERTIBLE: "sedan.svg",
    COUPE: "sedan.svg",
    "Luxury Sedan":"sedan.svg",
    "Luxury SUV":"sedan.svg",
    "Luxury MPV":"sedan.svg",
    "Sports car":"sedan.svg",
    "Hyper car":"sedan.svg",
  };
  return iconMap[bodyType] || "sedanCar.svg";
};

const getFuelTypeIcon = (fuelType: string): string => {
  const iconMap: Record<string, string> = {
    Petrol: "Petrol.svg",
    Diesel: "Diesel.svg",
    CNG: "CNG.svg",
    Electric: "Electric.svg",
  };
  return `${iconMap[fuelType] || "Petrol.svg"}`;
};

const getTransmissionIcon = (transmission: string): string => {
  const iconMap: Record<string, string> = {
    AUTOMATIC: "Automatic.svg",
    MANUAL: "manual.svg",
  };
  return `${iconMap[transmission] || "manual.svg"}`;
};

export const getBrowseCardConfigs = (
  filterData: ApiFilterResponse | null
): BrowseCardProps[] => {
  if (filterData) {
    return transformApiBrowseCardData(filterData);
  }
  return [];
};

export const getBrowseCardConfig = (
  index: number,
  browseCardConfigs?: BrowseCardProps[]
): BrowseCardProps | null => {
  if (!browseCardConfigs || browseCardConfigs.length === 0) {
    return null;
  }
  return browseCardConfigs[index % browseCardConfigs.length];
};
